/////////////////////////

import { last } from 'lodash';
import { BlocksNode } from '../types';
import { Cell, astar } from './astar';
import { Direction, RoutingInfo, Waypoint } from './types';

const outsideCost = (pt: Cell, target: BlocksNode) => {
  // cost of of cell to any edge of target

  const left = target.layoutData!.col - 1;
  const top = target.layoutData!.row - 1;
  const right = target.layoutData!.col + target.layoutData!.colSpan;
  const bottom = target.layoutData!.row + target.layoutData!.rowSpan;

  const h = Math.max(left - pt.col, 0) + Math.max(pt.col - right, 0);
  const v = Math.max(top - pt.row, 0) + Math.max(pt.row - bottom, 0);
  const diag =
    (pt.col <= left && (pt.row <= top || pt.row >= bottom)) ||
    (pt.col >= right && (pt.row <= top || pt.row >= bottom));

  return h + v + (diag ? 1 : 0);
};

const insideCost = (pt: Cell, target: BlocksNode) => {
  const left = target.layoutData!.col;
  const top = target.layoutData!.row;
  const right = left + target.layoutData!.colSpan - 1;
  const bottom = top + target.layoutData!.rowSpan - 1;

  const h = Math.min(pt.col - left, right - pt.col);
  const v = Math.min(pt.row - top, bottom - pt.row);

  return Math.min(h, v);
};

const getSiblingSeeds = (source: BlocksNode, target: BlocksNode) => {
  const src = source.layoutData;
  const tgt = target.layoutData;
  const seeds: Cell[] = [];

  if (!src || !tgt) {
    return [];
  }

  if (src.row + src.rowSpan < tgt.row) {
    /* src bottom is above tgt top; add seeds along src bottom */

    for (let i = 0; i < src.colSpan; ++i) {
      seeds.push({ col: src.col + i, row: src.row + src.rowSpan });
    }
  }

  if (src.row > tgt.row + tgt.rowSpan) {
    /* src top is below tgt bottom; add seeds along src top */

    for (let i = 0; i < src.colSpan; ++i) {
      seeds.push({ col: src.col + i, row: src.row - 1 });
    }
  }

  if (src.col + src.colSpan < tgt.col) {
    /* src right is left of tgt top; add seeds along src right */

    for (let i = 0; i < src.rowSpan; ++i) {
      seeds.push({ col: src.col + src.colSpan, row: src.row + i });
    }
  }

  if (src.col > tgt.col + tgt.colSpan) {
    /* src left is right of tgt right; add seeds along src left */

    for (let i = 0; i < src.rowSpan; ++i) {
      seeds.push({ col: src.col - 1, row: src.row + i });
    }
  }

  return seeds;
};

const getEdgeSeeds = (node: BlocksNode, edge: Direction) => {
  const seeds: Cell[] = [];

  if (node.layoutData) {
    const { col, row, colSpan, rowSpan } = node.layoutData;

    if (edge & Direction.Left) {
      for (let i = 0; i < rowSpan; ++i) {
        seeds.push({ col: col - 1, row: row + i });
      }
    }

    if (edge & Direction.Up) {
      for (let i = 0; i < colSpan; ++i) {
        seeds.push({ col: col + i, row: row - 1 });
      }
    }

    if (edge & Direction.Right) {
      for (let i = 0; i < rowSpan; ++i) {
        seeds.push({ col: col + colSpan, row: row + i });
      }
    }

    if (edge & Direction.Down) {
      for (let i = 0; i < colSpan; ++i) {
        seeds.push({ col: col + i, row: row + rowSpan });
      }
    }
  }

  return seeds;
};

const isCorner = (cells: Cell[], i: number, j: number, k: number) => {
  const ci = cells[i];
  const cj = cells[j];
  const ck = cells[k];

  return (
    (ci.row === cj.row && cj.row !== ck.row) ||
    (ci.col === cj.col && cj.col !== ck.col)
  );
};

const toWaypoints = (cells: Cell[]) => {
  const wp = {
    μcol: NaN,
    μrow: NaN,
  };

  const wps: Waypoint[] = [];

  let i = 0;
  wps.push({ col: cells[i].col, row: cells[i].row, ...wp });

  for (let j = 1; j < cells.length - 1; ++j) {
    if (isCorner(cells, i, j, j + 1)) {
      i = j;
      wps.push({ col: cells[i].col, row: cells[i].row, ...wp });
    }
  }

  i = cells.length - 1;
  wps.push({ col: cells[i].col, row: cells[i].row, ...wp });

  return wps;
};

const outsideEdge = (node: BlocksNode, cell: Cell) => {
  const layoutData = node.layoutData;
  const { col, row, colSpan, rowSpan } = layoutData!;

  switch (cell.col) {
    case col - 1:
      return Direction.Left;
    case col + colSpan:
      return Direction.Right;
  }

  switch (cell.row) {
    case row - 1:
      return Direction.Up;
    case row + rowSpan:
      return Direction.Down;
  }

  return Direction.None;
};

const insideEdge = (node: BlocksNode, cell: Cell) => {
  const layoutData = node.layoutData;
  const { col, row, colSpan, rowSpan } = layoutData!;

  switch (cell.col) {
    case col:
      return Direction.Left;
    case col + colSpan - 1:
      return Direction.Right;
  }

  switch (cell.row) {
    case row:
      return Direction.Up;
    case row + rowSpan - 1:
      return Direction.Down;
  }

  return Direction.None;
};

const endcell = (cell: Cell, edge: Direction) => {
  switch (edge) {
    case Direction.Left:
      return { col: cell.col + 1, row: cell.row };
    case Direction.Up:
      return { col: cell.col, row: cell.row + 1 };
    case Direction.Right:
      return { col: cell.col - 1, row: cell.row };
    case Direction.Down:
      return { col: cell.col, row: cell.row - 1 };
  }

  return cell;
};

const endwaypoint = (waypoint: Waypoint, edge: Direction) => {
  switch (edge) {
    case Direction.Left:
      waypoint.μcol = -Infinity;
      break;
    case Direction.Up:
      waypoint.μrow = -Infinity;
      break;
    case Direction.Right:
      waypoint.μcol = +Infinity;
      break;
    case Direction.Down:
      waypoint.μrow = +Infinity;
      break;
  }
};

const inAndOutWaypoints = (
  target: BlocksNode,
  waypoints: Waypoint[],
  edge: Direction
) => {
  if (!target.layoutData || waypoints.length < 2) {
    return;
  }

  const { col, row, colSpan, rowSpan } = target.layoutData;
  const wp = waypoints[waypoints.length - 1];

  switch (edge) {
    case Direction.Left:
      wp.col = col - 1;
      wp.μcol = NaN;

      waypoints.push({
        col: col - 1,
        row: row - 1,
        μcol: NaN,
        μrow: NaN,
      }); // left top corner
      waypoints.push({
        col: col,
        row: row - 1,
        μcol: NaN,
        μrow: NaN,
      });
      waypoints.push({
        col: col,
        row: row,
        μcol: NaN,
        μrow: -Infinity,
      });
      break;

    case Direction.Up:
      wp.row = row - 1;
      wp.μcol = NaN;

      waypoints.push({
        col: col - 1,
        row: row - 1,
        μcol: NaN,
        μrow: NaN,
      }); // left top corner
      waypoints.push({
        col: col - 1,
        row: row,
        μcol: NaN,
        μrow: NaN,
      }); // on left edge
      waypoints.push({
        col: col,
        row: row,
        μcol: -Infinity,
        μrow: NaN,
      }); // in left edge
      break;

    case Direction.Right:
      wp.col = col + colSpan;
      wp.μcol = NaN;

      waypoints.push({
        col: col + colSpan,
        row: row + rowSpan,
        μcol: NaN,
        μrow: NaN,
      }); // right bottom corner
      waypoints.push({
        col: col + colSpan - 1,
        row: row + rowSpan,
        μcol: NaN,
        μrow: NaN,
      }); // on bottom edge
      waypoints.push({
        col: col + colSpan - 1,
        row: row + rowSpan - 1,
        μcol: NaN,
        μrow: +Infinity,
      }); // on bottom edge
      break;

    case Direction.Down:
      wp.row = row + rowSpan;
      wp.μrow = NaN;

      waypoints.push({
        col: col + colSpan,
        row: row + rowSpan,
        μcol: NaN,
        μrow: NaN,
      }); // right bottom corner
      waypoints.push({
        col: col + colSpan,
        row: row + rowSpan - 1,
        μcol: NaN,
        μrow: NaN,
      }); // on right edge
      waypoints.push({
        col: col + colSpan - 1,
        row: row + rowSpan - 1,
        μcol: +Infinity,
        μrow: NaN,
      }); // in right edge
      break;
  }
};

const routeOut = (
  source: BlocksNode,
  target: BlocksNode,
  routingInfo: RoutingInfo
) => {
  /* build the whitelist */

  const whitelist = new Set<BlocksNode>();

  for (let p: BlocksNode | null = source.parent; p !== null; p = p.parent) {
    whitelist.add(p);
  }

  /* build the route */

  const seeds = getEdgeSeeds(source, Direction.All);
  const blockGrid = routingInfo.blockGrid;
  const isValid = (cell: Cell) =>
    !blockGrid.isBlocked(cell.col, cell.row, whitelist);
  const cost = (cell: Cell) => insideCost(cell, target);
  const cells = [...astar(seeds, isValid, cost)].reverse();

  if (cells.length < 1) {
    return [];
  }

  /* add endpoint cell */

  const targetEdge = insideEdge(target, last(cells)!);
  const sourceEdge = outsideEdge(source, cells[0]);
  cells.unshift(endcell(cells[0], sourceEdge));

  /* create waypoints */

  const waypoints = toWaypoints(cells);

  endwaypoint(waypoints[0], sourceEdge);
  inAndOutWaypoints(target, waypoints, targetEdge);

  return waypoints;
};

export const routeSelf = (
  node: BlocksNode,
  _target: BlocksNode,
  _routingInfo: RoutingInfo
) => {
  const wp: Waypoint[] = [];
  const { layoutData, parent } = node;

  if (!layoutData || !parent) {
    return wp;
  }

  let col = layoutData.col;
  let μcol = NaN;

  let row = layoutData.row + layoutData.rowSpan - 1;
  let μrow = +Infinity;

  wp.push({
    col: col,
    μcol: μcol,
    row: row,
    μrow: μrow,
  }); // a

  row = layoutData.row + layoutData.rowSpan;
  μrow = NaN;

  wp.push({
    col: col,
    μcol: μcol,
    row: row,
    μrow: μrow,
  }); // b

  col = layoutData.col - 1;
  μcol = NaN;
  wp.push({
    col: col,
    μcol: μcol,
    row: row,
    μrow: μrow,
  }); // c

  row = layoutData.row + layoutData.rowSpan - 1;
  μrow = NaN;
  wp.push({
    col: col,
    μcol: μcol,
    row: row,
    μrow: μrow,
  }); // d

  col = layoutData.col;
  μcol = -Infinity;
  wp.push({
    col: col,
    μcol: μcol,
    row: row,
    μrow: μrow,
  }); // e

  return wp;
};

export const routeAscendant = (
  source: BlocksNode,
  target: BlocksNode,
  routingInfo: RoutingInfo
): Waypoint[] => {
  return routeOut(source, target, routingInfo);
};

export const routeDescendant = (
  source: BlocksNode,
  target: BlocksNode,
  routingInfo: RoutingInfo
): Waypoint[] => {
  return routeOut(target, source, routingInfo).reverse();
};

export const routeSiblings = (
  source: BlocksNode,
  target: BlocksNode,
  routingInfo: RoutingInfo
) => {
  /* build the whitelist */

  const whitelist = new Set<BlocksNode>();

  for (let p: BlocksNode | null = source; p !== null; p = p.parent) {
    whitelist.add(p);
  }

  for (let p: BlocksNode | null = target; p !== null; p = p.parent) {
    whitelist.add(p);
  }

  const blockGrid = routingInfo.blockGrid;

  /* run astar backwards to avoid reversing everything */

  const seeds = getSiblingSeeds(target, source);
  const isValid = (cell: Cell) =>
    blockGrid.isInside(cell.col, cell.row) &&
    !blockGrid.isBlocked(cell.col, cell.row, whitelist);

  const h = (cell: Cell) => outsideCost(cell, source);
  const cells = [...astar(seeds, isValid, h)];

  if (cells.length < 1) {
    return [];
  }

  /* add endpoint cells */

  const sourceEdge = outsideEdge(source, cells[0]);
  cells.unshift(endcell(cells[0], sourceEdge));

  const targetEdge = outsideEdge(target, last(cells)!);
  cells.push(endcell(last(cells)!, targetEdge));

  const waypoints = toWaypoints(cells);

  /* guarantee the infinity semantics on the endpoints */

  endwaypoint(waypoints[0], sourceEdge);
  endwaypoint(last(waypoints)!, targetEdge);

  return waypoints;
};
